import React, { useEffect, useState } from "react"
import API from '../Common/API'
import { Route, Switch, Redirect } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { setLoggedIn, setMenuItems, setToken, setUserDetails, setAccountType, setEducationList, setFullEducations, setZodiacSigns, setFlaws, setTFlaws, setStatus, setTStatus, setOccupation, setTOccupation, setFullOccupation, setDhisa, setAllStars, setTDhisa } from '../Store/Auth/actions'
import Alert from '../Components/Alerts'
import { setAlert, setLanguage } from '../Store/Theme/actions'
import HomeRoot from "./HomeRoot";
import Login from "../Pages/Auth/Login";
import MemberRegister from "../Pages/Auth/MemberRegister";
import MemberHomeRoot from "./MemberHomeRoot";
import Loaders from "../Components/Loaders/Loaders";

// Website landing page — users log in from here, then are redirected back with ?token=
const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || 'http://localhost:3000'
const LANDING_PAGE = `${WEBSITE_URL}/landing/index.html`

// Redirect unauthenticated visitors to the website login page
const RedirectToLanding = () => {
    useEffect(() => { window.location.replace(LANDING_PAGE) }, [])
    return null
}

const MASTER_DATA_CACHE_KEY = 'matrimony_master_data_v2'
const MASTER_DATA_CACHE_TTL = 6 * 60 * 60 * 1000

const Root = () => {

    const [loading, setLoading] = useState(false)
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn)
    const dispatch = useDispatch();
    const [isLoggedChecked, setIsLoggedChecked] = useState(false);
    const setHideAlert = () => dispatch(setAlert({ active: false }));
    const alert = useSelector((state) => state.theme.alert)
    const [routes, setRoutes] = useState()
    const accountType = localStorage.getItem('accountType')

    const applyMasterData = (data) => {
        if (!data) return

        const educations = data.educations || []
        const zodiacSigns = data.zodiacSigns || []
        const flaws = data.flaws || []
        const statusList = data.statusList || []
        const occupations = data.occupations || []
        const stars = data.stars || []
        const dhisa = data.dhisa || []

        dispatch(setEducationList(educations.map((c) => ({ text: `${c.education} - ${c.specialization}`, value: c._id }))))
        dispatch(setFullEducations(educations))
        dispatch(setZodiacSigns(zodiacSigns.map((c) => ({ text: `${c.zodiacSign}`, value: c._id }))))
        dispatch(setFlaws(flaws.map((c) => ({ text: c.flawName || c.flaw || c.name || c._id, value: c._id }))))
        dispatch(setTFlaws(flaws.map((c) => ({ text: c.tFlawName || c.tFlaw || c.flawName || c.flaw || c._id, value: c._id }))))
        dispatch(setStatus(statusList.map((c) => ({ text: c.status, value: c._id }))))
        dispatch(setTStatus(statusList.map((c) => ({ text: c.tStatus, value: c._id }))))
        dispatch(setOccupation(occupations.map((c) => ({ text: c.occupation, value: c._id }))))
        dispatch(setTOccupation(occupations.map((c) => ({ text: c.tOccupation, value: c._id }))))
        dispatch(setFullOccupation(occupations))
        dispatch(setDhisa(dhisa.map((c) => ({ text: c.dhisa, value: c.dhisaId })))
        )
        dispatch(setTDhisa(dhisa.map((c) => ({ text: c.tDhisa, value: c.dhisaId }))))
        dispatch(setAllStars(stars.map((c) => ({ text: c.starName || c.star, value: c._id }))))
    }

    const getCachedMasterData = () => {
        try {
            const cached = sessionStorage.getItem(MASTER_DATA_CACHE_KEY)
            if (!cached) return null

            const parsed = JSON.parse(cached)
            if (!parsed?.timestamp || Date.now() - parsed.timestamp > MASTER_DATA_CACHE_TTL) return null
            return parsed.data
        } catch {
            return null
        }
    }

    const fetchMasterData = async () => {
        const results = await Promise.allSettled([
            API.get('/common/getEducations'),
            API.get('/common/getZodiacSign'),
            API.get('/common/getAllFlaws'),
            API.get('/common/getStatusList'),
            API.get('/common/getOccupations'),
            API.get('/common/getAllStars'),
            API.get('/common/getDhisa'),
        ])

        const safeData = (result) => (result.status === 'fulfilled' ? result.value?.data?.data || [] : [])

        const data = {
            educations: safeData(results[0]),
            zodiacSigns: safeData(results[1]),
            flaws: safeData(results[2]),
            statusList: safeData(results[3]),
            occupations: safeData(results[4]),
            stars: safeData(results[5]),
            dhisa: safeData(results[6]),
        }

        sessionStorage.setItem(MASTER_DATA_CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data }))
        applyMasterData(data)
    }

    const loadMasterDataFast = () => {
        const cached = getCachedMasterData()
        if (cached) applyMasterData(cached)

        const refreshTimer = window.setTimeout(() => {
            fetchMasterData().catch((error) => {
                if (!cached) console.error('Unable to load master data', error)
            })
        }, cached ? 1500 : 800)

        return () => window.clearTimeout(refreshTimer)
    }

    useEffect(() => {
        // adding interceptors to api
        API.interceptors.request.use((request) => {
            setLoading(true);
            // dispatch(setLoading(true))
            return request;
        });
        API.interceptors.response.use(
            (response) => {
                setLoading(false);
                // dispatch(setLoading(false))
                return response;
            },
            (error) => {
                setLoading(false);
                // dispatch(setLoading(false))
                return Promise.reject(error);
            }
        );
    }, []); // eslint-disable-line

    const checkLoggedIn = () => {
        let lang = localStorage.getItem('i18nextLng')
        lang !== 'null' ? dispatch(setLanguage(lang)) : dispatch(setLanguage('en'))

        // Pick up token passed from the website landing page via ?token=
        const urlParams = new URLSearchParams(window.location.search)
        const urlToken = urlParams.get('token')
        if (urlToken) {
            localStorage.setItem('token', urlToken)
            localStorage.setItem('accountType', 'member')
            // Clean the token out of the URL without a page reload
            window.history.replaceState({}, '', window.location.pathname)
        }

        // Always read accountType fresh from localStorage (URL token may have just set it)
        const currentAccountType = localStorage.getItem('accountType')

        let token = localStorage.getItem('token')
        if (!token && isLoggedIn) {
            dispatch(setLoggedIn(false));
            setIsLoggedChecked(true)
        } else if (token && token.split(".").length > 1) {
            try {
                let userDetails = JSON.parse(atob(token.split(".")[1]));
                dispatch(setLoggedIn(true))
                dispatch(setToken(token))
                dispatch(setMenuItems(JSON.parse(localStorage.getItem('menuItems')) || []))
                localStorage.setItem("userDetails", JSON.stringify(userDetails));
                dispatch(setAccountType(currentAccountType))
                dispatch(setUserDetails(userDetails))
            } catch (err) {
                console.error('Invalid auth token in storage, clearing session', err)
                localStorage.removeItem('token')
                localStorage.removeItem('userDetails')
                localStorage.removeItem('menuItems')
                dispatch(setLoggedIn(false))
            }
            setIsLoggedChecked(true);
        } else {
            setIsLoggedChecked(true)
        }
    }



    useEffect(() => {
        checkLoggedIn()
    }, []) // eslint-disable-line

    useEffect(() => {
        if (isLoggedIn && accountType !== 'member') return loadMasterDataFast()
    }, [accountType, isLoggedIn]) // eslint-disable-line

    useEffect(() => {
        if (isLoggedIn && accountType === 'member') {
            setRoutes(
                <Switch>
                    <Route path="/member" component={MemberHomeRoot} />
                    <Redirect to="/member" />
                </Switch>
            )
        } else if (isLoggedIn) {
            setRoutes(
                <Switch>
                    <Route path="/" component={HomeRoot} />
                    <Redirect to="/" />
                </Switch>
            )
        } else {
            setRoutes(
                <Switch>
                    <Route path="/member/register" component={MemberRegister} />
                    <Route path="/auth/login" component={Login} />
                    <Route component={RedirectToLanding} />
                </Switch>
            )
        }
    }, [accountType, isLoggedIn])

    return (
        <>
            {isLoggedChecked ? <div className="h-100">
                <Alert title={alert.title} subtitle={alert.subtitle} active={alert.active} setActive={setHideAlert} type={alert.type} />
                {routes}
            </div> : <Loaders />}
        </>
    )
}

export default Root
